const STICKY_SCROLL_THRESHOLD_PX = 48;

export class ChatScrollController {
    private readonly imagesBoundForScroll = new WeakSet<HTMLImageElement>();
    private bottomScrollFrame: number | null = null;
    private bottomScrollSettleUntil = 0;
    private bottomScrollLastTop = 0;

    constructor(private readonly messages: HTMLElement) {
    }

    scrollToBottom(): void {
        this.scheduleBottomScrollSettle();
        this.bindImageScrollHandlers();
    }

    isNearBottom(): boolean {
        const remaining = this.messages.scrollHeight - this.messages.clientHeight - this.messages.scrollTop;
        return remaining <= STICKY_SCROLL_THRESHOLD_PX;
    }

    shouldFollowBottom(): boolean {
        return this.bottomScrollFrame !== null || this.isNearBottom();
    }

    destroy(): void {
        this.cancelBottomScrollSettle();
    }

    private scheduleBottomScrollSettle(): void {
        this.bottomScrollSettleUntil = Math.max(this.bottomScrollSettleUntil, performance.now() + 800);
        if (this.bottomScrollFrame !== null) {
            return;
        }

        this.bottomScrollLastTop = 0;
        this.settleBottomScroll();
    }

    private settleBottomScroll(): void {
        const maxScrollTop = Math.max(0, this.messages.scrollHeight - this.messages.clientHeight);
        const expectedTop = Math.min(this.bottomScrollLastTop, maxScrollTop);
        if (
            this.bottomScrollLastTop > 0
            && this.messages.scrollTop + STICKY_SCROLL_THRESHOLD_PX < expectedTop
        ) {
            this.cancelBottomScrollSettle();
            return;
        }

        this.messages.scrollTop = maxScrollTop;
        this.bottomScrollLastTop = this.messages.scrollTop;
        this.bindImageScrollHandlers();

        if (performance.now() >= this.bottomScrollSettleUntil) {
            this.bottomScrollFrame = null;
            this.bottomScrollLastTop = 0;
            return;
        }

        this.bottomScrollFrame = window.requestAnimationFrame(() => {
            this.settleBottomScroll();
        });
    }

    private cancelBottomScrollSettle(): void {
        if (this.bottomScrollFrame !== null) {
            window.cancelAnimationFrame(this.bottomScrollFrame);
            this.bottomScrollFrame = null;
        }
        this.bottomScrollSettleUntil = 0;
        this.bottomScrollLastTop = 0;
    }

    private bindImageScrollHandlers(): void {
        this.messages.querySelectorAll<HTMLImageElement>('img').forEach(img => {
            if (this.imagesBoundForScroll.has(img)) {
                return;
            }

            this.imagesBoundForScroll.add(img);
            const scrollTopBeforeImageLoad = this.messages.scrollTop;
            const scrollIfStillAtBottom = (): void => {
                if (this.messages.scrollTop + 1 >= scrollTopBeforeImageLoad) {
                    this.scrollToBottom();
                }
            };

            if (img.complete) {
                window.requestAnimationFrame(scrollIfStillAtBottom);
                return;
            }

            img.addEventListener('load', scrollIfStillAtBottom, {once: true});
            img.addEventListener('error', scrollIfStillAtBottom, {once: true});
        });
    }
}
